import classNames from 'classnames';
import { ConnectedRouter } from 'connected-next-router';
import Router from 'next/router';
import { memo, type ReactNode, useRef } from 'react';

import { MobileNavigationContextProvider } from '@air/component-mobile-navigation';
import { ActionBarProvider } from '@air/provider-action-bar';
import { useBreakpointsContext } from '@air/provider-media-query';
import { ModalProvider } from '@air/provider-modal';
import { useToasts } from '@air/provider-toast';
import { SysadminProvider } from '@air/sysadmin';
import { ErrorBoundary } from '@air/utils-error';

import { AccountProvisionLayout } from '~/components/AccountProvisioner/components/AccountProvisionLayout';
import { AccountProvisionerProvider } from '~/components/AccountProvisioner/providers/AccountProvisionerProvider';
import { DevModalProvider } from '~/components/DevModal/DevModalProvider';
import { Banners } from '~/components/Layouts/HomeLayout/Banners';
import { useCheckIfUserHasWorkspaces } from '~/components/Layouts/HomeLayout/hooks/useCheckIfUserHasWorkspaces';
import { useConfigureThirdPartyServices } from '~/components/Layouts/HomeLayout/hooks/useConfigureThirdPartyServices';
import { useCreateBoardFromCanvasSearchParams } from '~/components/Layouts/HomeLayout/hooks/useCreateBoardFromCanvasSearchParams';
import { useCreateCanvasFromSearchParams } from '~/components/Layouts/HomeLayout/hooks/useCreateCanvasFromSearchParams';
import { useShowDemoFormModal } from '~/components/Layouts/HomeLayout/hooks/useDemoFormModal';
import { useHandleConfigureSignoutUrl } from '~/components/Layouts/HomeLayout/hooks/useHandleConfigureSignoutUrl.ts';
import { useHandleInactivity } from '~/components/Layouts/HomeLayout/hooks/useHandleInactivity';
import { useHandleSkipOnboarding } from '~/components/Layouts/HomeLayout/hooks/useHandleSkipOnboarding';
import { useHandleUnauthenticatedHomeLayoutUser } from '~/components/Layouts/HomeLayout/hooks/useHandleUnauthenticatedHomeLayoutUser.ts';
import { useHandleUserNeedsRedirect } from '~/components/Layouts/HomeLayout/hooks/useHandleUserNeedsRedirect';
import { useNotificationsEvent } from '~/components/Layouts/HomeLayout/hooks/useNotificationsEvent';
import { useScheduleClearAuthResumeState } from '~/components/Layouts/HomeLayout/hooks/useScheduleClearAuthResumeState';
import { useShowOnboardingUploadPrompt } from '~/components/Layouts/HomeLayout/hooks/useShowOnboardingUploadPrompt';
import { useSubmitPendingOnboardingUpload } from '~/components/Layouts/HomeLayout/hooks/useSubmitPendingOnboardingUpload';
import { SideNav } from '~/components/Layouts/HomeLayout/SideNav/SideNav';
import { SkipOnboardingLoadingOverlay } from '~/components/Layouts/HomeLayout/SkipOnboardingLoadingOverlay';
import { getPendingOnboardingUploadFilesLayout } from '~/components/Layouts/pending-onboarding-upload-files-layout/PendingOnboardingUploadFilesLayout';
import { MaintenanceMode } from '~/components/MaintenanceMode/MaintenanceMode';
import { CoreMobileNavigation } from '~/components/Mobile/CoreMobileNavigation';
import { PrivateDropzone } from '~/components/PrivateDropzone/PrivateDropzone';
import { PrivateReduxProvider } from '~/components/PrivateReduxProvider';
import { PrivateSocketContextProvider } from '~/components/PrivateSocketContextProvider';
import { PrivateUploadsActivityContainer } from '~/components/PrivateUploadsActivityContainer';
import { PrivateWorkspaceActivityContainer } from '~/components/PrivateWorkspaceActivityContainer';
import { ReactDndDragLayer } from '~/components/ReactDndDragLayer/ReactDndDragLayer';
import { SelectedItemsHydration } from '~/components/SelectedItemsHydration';
import { MainContent } from '~/components/Shared/ClipGallery/Content';
import { PrivateSavedSizesCatalogProvider } from '~/components/v2/components/saved-settings/PrivateSavedSizesCatalogProvider';
import { KanbanOffsetContextProvider } from '~/components/v2/components/shared/kanban/KanbanOffsetContextProvider';
import { PrivatePageSeoTitle } from '~/components/v2/components/shared/seo/PrivatePageSeoTitle';
import { TableHeaderPositionContextProvider } from '~/components/v2/components/shared/table/TableHeaderPositionContextProvider';
import { useShowUI2Page } from '~/components/v2/hooks/useShowUI2Page';
import { useUI2026Styles } from '~/components/v2/hooks/useUI2026Styles';
import { PrivateDefaultLayout } from '~/components/v2/layouts/PrivateDefaultLayout';
import { WorkspaceLockedOverlay } from '~/components/WorkspaceLockedOverlay/WorkspaceLockedOverlay';
import { WorkspaceOnboardingUploadPromptGate } from '~/components/WorkspaceOnboardingForm/WorkspaceOnboardingUploadPromptGate';
import { Routes } from '~/constants/routes';
import { inter } from '~/fonts/inter';
import { useOnLocationChange } from '~/hooks/router/useOnLocationChange';
import { BillingCycleProvider } from '~/hooks/useBillingCycle';
import { useBroadcastCreditStats } from '~/hooks/useBroadcastCreditStats';
import { useHandleSessionExpired } from '~/hooks/useHandleSessionExpired';
import { useHandleWorkspaceAddOnsUpdated } from '~/hooks/useHandleWorkspaceAddOnsUpdated';
import { useHandleWorkspaceCreditsConsumed } from '~/hooks/useHandleWorkspaceCreditsConsumed';
import { useHandleWorkspaceSkillsUpdated } from '~/hooks/useHandleWorkspaceSkillsUpdated';
import { useRedirectUnverifiedUser } from '~/hooks/useRedirectUnverifiedUser';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { ThumbnailPreferenceContextProvider } from '~/hooks/useThumbnailPreference';
import { AccountProvider } from '~/providers/AccountProvider';
import { CannyProvider } from '~/providers/CannyProvider';
import { ClipTranscriptFetchQueueProvider } from '~/providers/ClipTranscriptFetchQueueProvider';
import { CurrentWorkspaceMemberProvider } from '~/providers/CurrentWorkspaceMemberProvider';
import { CurrentWorkspacePermissionsProvider } from '~/providers/CurrentWorkspacePermissionsProvider';
import { CurrentWorkspaceProvider, useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { DesktopAppConnectionProvider } from '~/providers/DesktopAppConnectionProvider';
import { ExternalLinksClickableProvider } from '~/providers/ExternalLinksClickableProvider';
import { FiltersProvider, type GetImmutableFilters } from '~/providers/FiltersProvider';
import { InactivityProvider } from '~/providers/InactivityProvider';
import { ModifiersKeysPressedProvider } from '~/providers/ModifiersKeysPressedProvider';
import { MouseSelectionProvider } from '~/providers/MouseSelectionProvider';
import { PrivateCustomFieldsProvider } from '~/providers/PrivateCustomFieldsProvider';
import { PrivateSearchProvider } from '~/providers/PrivateSearchProvider';
import { SearchHeaderHeightProvider } from '~/providers/SearchHeaderHeightProvider';
import { SideNavProvider } from '~/providers/SideNavProvider';
import { SubscriptionProvider } from '~/providers/SubscriptionProvider';
import { WorkspaceMembersProvider } from '~/providers/WorkspaceMembersProvider';
import { WorkspaceRolesProvider } from '~/providers/WorkspaceRolesProvider';

export type HomeLayoutProps = {
  bottomBar?: ReactNode;
  children: ReactNode;
  getLibraryActions?: boolean;
  headerActions?: ReactNode;
  /** This page paints its own rail on mobile, and on desktop when ui2026 is off. */
  ownRail?: boolean;
  search?: ReactNode;
};

export type HomeLayoutOptions = {
  bottomBar?: ReactNode;
  getImmutableFilters?: GetImmutableFilters;
  search?: ReactNode;
  supportsSmartSearch?: boolean;
  headerActions?: ReactNode;
  getLibraryActions?: boolean;
  /** Settings is not a file drop target. */
  disableDrop?: boolean;
  /** Settings scrolls inside a viewport-sized frame. Library pages fill their parent. */
  fillViewport?: boolean;
  /** This page paints its own rail on mobile, and on desktop when ui2026 is off. */
  ownRail?: boolean;
};

const EMPTY_IMMUTABLE_FILTERS = {};

const getDefaultImmutableFilters: GetImmutableFilters = () => EMPTY_IMMUTABLE_FILTERS;

const redirectSysadminOnWorkspaceChange = () => {
  // Rejection only happens if a later navigation aborts this one; the sysadmin callback cannot recover.
  void Router.push(Routes.media.all);
};

const HomeLayout = memo(
  ({ bottomBar, children, getLibraryActions, headerActions, ownRail = false, search }: HomeLayoutProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { dismissAllToasts } = useToasts();
    const { ui2026 } = useTemporaryFeatureFlags();
    const { showUI2 } = useShowUI2Page();
    // While the flag is still loading, desktop keeps the workspace rail so it does not mount twice.
    const paintsOwnRail = ownRail && (!showUI2 || ui2026 === false);
    // Stays mounted when PrivateDefaultLayout does not (mobile, classic settings).
    // Desktop workspace chrome always keeps data-ui2026; this effect runs after the layout's.
    useUI2026Styles({ enabled: showUI2, force: showUI2 && !paintsOwnRail });
    const { showOnboardingUploadPrompt } = useShowOnboardingUploadPrompt();
    const selectionAreaRef = useRef<HTMLDivElement | null>(null);
    useSubmitPendingOnboardingUpload();
    const { isLoading: isSkipOnboardingLoading } = useHandleSkipOnboarding();
    useScheduleClearAuthResumeState();

    useHandleConfigureSignoutUrl();
    useHandleInactivity();
    useCheckIfUserHasWorkspaces();
    useCreateCanvasFromSearchParams();
    useCreateBoardFromCanvasSearchParams();
    useHandleUserNeedsRedirect();
    useRedirectUnverifiedUser();
    useHandleUnauthenticatedHomeLayoutUser();
    useShowDemoFormModal();
    useNotificationsEvent();
    useHandleWorkspaceAddOnsUpdated();
    useHandleWorkspaceSkillsUpdated();
    useHandleSessionExpired();
    useHandleWorkspaceCreditsConsumed();
    useBroadcastCreditStats();
    useOnLocationChange(dismissAllToasts);
    useConfigureThirdPartyServices();

    if (paintsOwnRail) {
      return <>{children}</>;
    }

    if (showUI2) {
      return (
        <ClipTranscriptFetchQueueProvider>
          <PrivateDefaultLayout
            bottomBar={bottomBar}
            search={search}
            headerActions={headerActions}
            getLibraryActions={getLibraryActions}
          >
            {children}

            <PrivateWorkspaceActivityContainer />
            <PrivateUploadsActivityContainer />
          </PrivateDefaultLayout>
          {showOnboardingUploadPrompt && <WorkspaceOnboardingUploadPromptGate />}
          {isSkipOnboardingLoading && <SkipOnboardingLoadingOverlay />}
        </ClipTranscriptFetchQueueProvider>
      );
    }

    return (
      <ClipTranscriptFetchQueueProvider>
        <div className="relative flex h-full overflow-y-hidden">
          <PrivatePageSeoTitle isFallbackTitle />

          {isAboveMediumScreen && <SideNav />}
          <MouseSelectionProvider selectionArea={selectionAreaRef}>
            <MainContent className="ads:bg-base bg-gray-1" data-testid="MAIN" ref={selectionAreaRef}>
              {currentWorkspace && <Banners />}
              {children}
            </MainContent>
          </MouseSelectionProvider>

          {!isAboveMediumScreen && <CoreMobileNavigation />}

          <PrivateWorkspaceActivityContainer />
          <PrivateUploadsActivityContainer />
          {showOnboardingUploadPrompt && <WorkspaceOnboardingUploadPromptGate />}
          {isSkipOnboardingLoading && <SkipOnboardingLoadingOverlay />}
        </div>
      </ClipTranscriptFetchQueueProvider>
    );
  },
);

HomeLayout.displayName = 'HomeLayout';

export const getLayout = (
  page: ReactNode,
  {
    bottomBar,
    getImmutableFilters = getDefaultImmutableFilters,
    search,
    supportsSmartSearch = false,
    headerActions,
    getLibraryActions,
    disableDrop = false,
    fillViewport = false,
    ownRail = false,
  }: HomeLayoutOptions = {},
) =>
  getPendingOnboardingUploadFilesLayout(
    <div className={classNames(fillViewport ? 'h-screen overflow-hidden' : 'h-full', inter.className, inter.variable)}>
      <ExternalLinksClickableProvider areExternalLinksClickable>
        <AccountProvisionerProvider>
          <AccountProvisionLayout>
            <AccountProvider>
              <PrivateReduxProvider>
                <ErrorBoundary>
                  <ConnectedRouter>
                    <SysadminProvider onRedirectOnWorkspaceChange={redirectSysadminOnWorkspaceChange}>
                      <BillingCycleProvider>
                        <CurrentWorkspaceProvider>
                          <CurrentWorkspacePermissionsProvider>
                            <SearchHeaderHeightProvider>
                              <SubscriptionProvider>
                                <PrivateSavedSizesCatalogProvider>
                                  <WorkspaceMembersProvider>
                                    <WorkspaceRolesProvider>
                                      <CurrentWorkspaceMemberProvider>
                                        <PrivateCustomFieldsProvider>
                                          <BillingCycleProvider>
                                            <InactivityProvider>
                                              <FiltersProvider getImmutableFilters={getImmutableFilters}>
                                                <ModifiersKeysPressedProvider>
                                                  <ThumbnailPreferenceContextProvider>
                                                    <ModalProvider>
                                                      <DevModalProvider>
                                                        <PrivateSearchProvider
                                                          supportsSmartSearch={supportsSmartSearch}
                                                        >
                                                          <PrivateDropzone disableDrop={disableDrop}>
                                                            <PrivateSocketContextProvider>
                                                              <CannyProvider>
                                                                <DesktopAppConnectionProvider>
                                                                  <ActionBarProvider>
                                                                    <SideNavProvider>
                                                                      <MobileNavigationContextProvider>
                                                                        <MaintenanceMode>
                                                                          <WorkspaceLockedOverlay>
                                                                            <TableHeaderPositionContextProvider>
                                                                              <KanbanOffsetContextProvider>
                                                                                <HomeLayout
                                                                                  bottomBar={bottomBar}
                                                                                  search={search}
                                                                                  headerActions={headerActions}
                                                                                  getLibraryActions={getLibraryActions}
                                                                                  ownRail={ownRail}
                                                                                >
                                                                                  {page}
                                                                                </HomeLayout>
                                                                              </KanbanOffsetContextProvider>
                                                                            </TableHeaderPositionContextProvider>
                                                                          </WorkspaceLockedOverlay>
                                                                        </MaintenanceMode>
                                                                      </MobileNavigationContextProvider>
                                                                    </SideNavProvider>
                                                                  </ActionBarProvider>
                                                                </DesktopAppConnectionProvider>
                                                              </CannyProvider>
                                                            </PrivateSocketContextProvider>
                                                          </PrivateDropzone>
                                                        </PrivateSearchProvider>
                                                      </DevModalProvider>
                                                      <ReactDndDragLayer />
                                                    </ModalProvider>
                                                  </ThumbnailPreferenceContextProvider>
                                                </ModifiersKeysPressedProvider>
                                              </FiltersProvider>
                                            </InactivityProvider>
                                          </BillingCycleProvider>
                                        </PrivateCustomFieldsProvider>
                                      </CurrentWorkspaceMemberProvider>
                                    </WorkspaceRolesProvider>
                                  </WorkspaceMembersProvider>
                                </PrivateSavedSizesCatalogProvider>
                              </SubscriptionProvider>
                            </SearchHeaderHeightProvider>
                          </CurrentWorkspacePermissionsProvider>
                          <SelectedItemsHydration />
                        </CurrentWorkspaceProvider>
                      </BillingCycleProvider>
                    </SysadminProvider>
                  </ConnectedRouter>
                </ErrorBoundary>
              </PrivateReduxProvider>
            </AccountProvider>
          </AccountProvisionLayout>
        </AccountProvisionerProvider>
      </ExternalLinksClickableProvider>
    </div>,
  );
